// BUContext — global provider for the selected Business Unit.
// Persists selection in localStorage and attaches X-BU-Context header on
// every axios call so backend scoping can narrow by BU.
import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const LS_KEY = 'aeroguard.selectedBU';

const BUContext = createContext(null);

export function BUProvider({ children }) {
  const { api, isAuthenticated, user } = useAuth();
  const [units, setUnits] = useState([]);
  const [primary, setPrimary] = useState(null);
  const [selectedId, setSelectedId] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  // Attach a request interceptor that adds X-BU-Context: <selectedId>.
  useEffect(() => {
    const id = api.interceptors.request.use((config) => {
      const bu = (() => {
        try { return localStorage.getItem(LS_KEY); } catch { return null; }
      })();
      if (bu) config.headers['X-BU-Context'] = bu;
      return config;
    });
    return () => { api.interceptors.request.eject(id); };
  }, [api]);

  // Also register on the raw axios (for direct fetch fallbacks).
  useEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      try {
        const bu = localStorage.getItem(LS_KEY);
        if (bu) {
          config.headers = config.headers || {};
          config.headers['X-BU-Context'] = bu;
        }
      } catch {/* ignore */ }
      return config;
    });
    return () => { axios.interceptors.request.eject(id); };
  }, []);

  const refresh = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const r = await api.get('/me/business-units');
      setUnits(r.data.units || []);
      setPrimary(r.data.primary || null);
      // Ensure the persisted selection is still valid.
      const stored = localStorage.getItem(LS_KEY);
      if (stored && !(r.data.units || []).some((u) => u.id === stored)) {
        setSelectedId(null);
        try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
      }
    } catch {
      setUnits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [isAuthenticated, user?.id]);// eslint-disable-line react-hooks/exhaustive-deps

  const setBU = (id) => {
    setSelectedId(id || null);
    try {
      if (id) localStorage.setItem(LS_KEY, id);
      else localStorage.removeItem(LS_KEY);
    } catch {/* ignore */ }
  };

  const value = useMemo(() => ({
    units, primary, selectedId, loading, setBU, refresh,
  }), [units, primary, selectedId, loading]);

  return <BUContext.Provider value={value}>{children}</BUContext.Provider>;
}

export function useBU() {
  const ctx = useContext(BUContext);
  if (!ctx) throw new Error('useBU must be used inside <BUProvider>');
  return ctx;
}
